import { Injectable } from '@angular/core';
import { Observable, timer, EMPTY } from 'rxjs';
import { map } from 'rxjs/operators';
import moment from 'moment-timezone';
import { WorkSessionDTO } from '../../app/features/work-sessions/dtos/work-session.dto';
import { WorkSessionsApiService } from '@root/src/app/features/work-sessions/work-sessions-api.service';

@Injectable({ providedIn: 'root' })
export class WorkSessionService {
    private timerObservable$: Observable<moment.Duration> | null = null;
    private currentStartTime: string | null = null;

    constructor(
        private workSessionsApiService: WorkSessionsApiService
    ) {
    }

    public getActiveWorkSession(): Observable<WorkSessionDTO> {
        return this.workSessionsApiService.getActiveWorkSession();
    }

    public getWorkSessionTimer(startTime: string): Observable<moment.Duration> {
        if (!startTime) {
            return EMPTY;
        }

        // If start time changed, reset observable
        if (this.currentStartTime !== startTime) {
            this.currentStartTime = startTime;
            this.timerObservable$ = null;
        }

        // Create the timer if it doesn't exist
        if (!this.timerObservable$) {
            this.timerObservable$ = timer(0, 1000).pipe(
                map(() => this.getDuration(startTime))
            );
        }

        return this.timerObservable$;
    }

    public formatDuration(duration: moment.Duration, useShortFormat: boolean = false): string {
        const hours = duration.hours();
        const minutes = duration.minutes();
        const seconds = duration.seconds();

        if (useShortFormat) {
            return `${this.formatTwoDigits(hours)}:${this.formatTwoDigits(minutes)}:${this.formatTwoDigits(seconds)}`;
        }

        const hoursText = hours > 0 ? ` ${hours} hour${hours !== 1 ? 's' : ''},` : '';
        const minutesText = hours > 0 || minutes > 0 ? ` ${minutes} minute${minutes !== 1 ? 's' : ''}, and` : '';
        return `A work session started${hoursText}${minutesText} ${seconds} second${seconds !== 1 ? 's' : ''} ago.`;
    }

    public calculateHoursByTime(startTime: string, endTime: string): string {
        if (!startTime || !endTime) {
            return '';
        }

        const duration = this.getDuration(startTime, endTime);
        const hours = duration.hours();
        const minutes = duration.minutes();
        const seconds = duration.seconds();

        return `${this.formatTwoDigits(hours)}:${this.formatTwoDigits(minutes)}:${this.formatTwoDigits(seconds)}`;
    }

    private getDuration(startTime: string, endTime?: string): moment.Duration {
        const start = moment(startTime);
        const end = endTime ? moment(endTime) : moment();
        return moment.duration(end.diff(start));
    }

    private formatTwoDigits(num: number): string {
        return String(num).padStart(2, '0');
    }
}
